'use client';

import React from 'react';
import NextLink from 'next/link';
import { twMerge } from 'tailwind-merge';

interface LinkProps {
  children: React.ReactNode;
  href: string;
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' | 'inherit';
  underline?: 'none' | 'hover' | 'always';
  variant?:
    | 'body1'
    | 'body2'
    | 'button'
    | 'caption'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'inherit'
    | 'subtitle1'
    | 'subtitle2';
  target?: string;
  rel?: string;
  className?: string;
  component?: string | React.ElementType;
  onClick?: (event: any) => void | undefined;
  'aria-label'?: string;
}

export const Link: React.FC<LinkProps> = ({
  children,
  href = '#',
  color = 'primary',
  underline = 'hover',
  variant = 'inherit',
  target,
  rel,
  className = '',
  component: Component,
  onClick,
  'aria-label': ariaLabel,
  ...other
}) => {
  // Base classes
  const baseClasses = twMerge(
    'transition-colors duration-200',
    'font-normal',
    underline === 'none'
      ? 'no-underline'
      : underline === 'hover'
        ? 'no-underline hover:underline'
        : 'underline'
  );

  // Color classes
  const colorClasses = {
    primary: 'text-primary hover:primary-dark',
    secondary: 'text-purple-600 hover:text-purple-800',
    error: 'text-red-600 hover:text-red-800',
    warning: 'text-orange-600 hover:text-orange-800',
    info: 'text-blue-500 hover:text-blue-700',
    success: 'text-green-600 hover:text-green-800',
    inherit: 'text-inherit',
  }[color];

  // Variant classes (simplified, can be expanded as needed)
  const variantClasses = variant === 'inherit' ? '' : `text-${variant}`;

  // Combine all classes
  const classes = twMerge(baseClasses, colorClasses, variantClasses, className);

  // If a custom component is provided, use it
  if (Component) {
    return (
      <Component
        href={href}
        target={target}
        rel={rel}
        onClick={onClick}
        className={classes}
        aria-label={ariaLabel}
        {...other}
      >
        {children}
      </Component>
    );
  }

  // If it's an external link (starts with http or https)
  if (href?.startsWith('http')) {
    return (
      <a
        href={href}
        target={target || '_blank'}
        rel={rel || 'noopener noreferrer'}
        className={classes}
        onClick={onClick}
        aria-label={ariaLabel}
        {...other}
      >
        {children}
      </a>
    );
  }

  // Otherwise, use Next.js Link for internal navigation
  return (
    <NextLink href={href} className={classes} onClick={onClick} aria-label={ariaLabel} {...other}>
      {children}
    </NextLink>
  );
};

export default Link;
